import { cvReferencesAPI } from 'api/CvAPI'
import { IItemDraftHandle } from 'types/hocTypes'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ICv, IReference } from 'types/cvsInterfaces'
import withProfileCard from 'Components/reusable/HOC/withProfileCard'
import { getReferenceInitialData } from 'Components/reusable/DataContext/InitialData'
import { referenceSchema } from 'Components/reusable/DataContext/ValidationSchema'
import ReferenceItemCard from './ReferenceItemCard'
import ReferenceModal from './ReferenceModal'

/** @notExported */
interface IReferencesCardProps {
  /** Indicates if the card is editable. */
  editable: boolean
  /** The CV object */
  cv: ICv
  /** Callback function to set the updated date */
  setUpdatedAt: () => void
  /** Callback function to close an item */
  closeItem: () => void
  /** Callback function to replace or add an item */
  replaceOrAddItem: (newItem: IReference) => IReference[]
  /** Callback function to open an item */
  openItem: (item: IReference) => void
  /** The currently edited item */
  editedItem: IReference | null
  /** The list of items */
  items: IReference[]
  /** The header text */
  header: string
  /** Function to update CV progress. */
  updateProgress: () => void
  /** Dialog drafts from the CV wizard (see `withProfileCard`'s `itemDraft`). */
  itemDraft?: IItemDraftHandle
  /** Function to update references list. */
  setReferences: (items: IReference[]) => void
}

/**
 * The ReferencesCard component.
 *
 * @param editable - Indicates if the card is editable
 * @param cv - The CV object
 * @param setUpdatedAt - Callback function to set the updated date
 * @param closeItem - Callback function to close an item
 * @param replaceOrAddItem - Callback function to replace or add an item
 * @param openItem - Callback function to open an item
 * @param editedItem - The currently edited item
 * @param items - The list of items
 * @param header - The header text
 * @returns The ReferencesCard component.
 * @notExported
 */
const ReferencesCard: React.FC<IReferencesCardProps> = ({
  cv,
  editable,
  items,
  setUpdatedAt,
  closeItem,
  replaceOrAddItem,
  openItem,
  editedItem,
  header,
  updateProgress,
  itemDraft,
  setReferences,
}) => {
  const { i18n } = useTranslation()

  const itemCards = useMemo(
    () =>
      items.map((item, i) => (
        <ReferenceItemCard
          key={`${item.id}-${i18n.language}`}
          profileWarningId={`profile-item-references-${item.id}`}
          editable={editable}
          item={item}
          onOpen={() => openItem(item)}
          header={header}
          showDivider={i < items.length - 1}
        />
      )),
    [items, editable, header, openItem, i18n.language]
  )

  return (
    <>
      {itemCards}
      {editedItem !== undefined && (
        <ReferenceModal
          key={editedItem?.id ?? 'new'}
          item={editedItem}
          draft={itemDraft?.forItem(editedItem ?? null)}
          cvId={cv.id}
          initialData={getReferenceInitialData(cv.id)}
          schema={referenceSchema()}
          api={cvReferencesAPI}
          localeBase="profile.reference.modal"
          maxWidth="xl"
          onClose={(value?: Record<string, unknown> | { id?: number; name?: string } | { newItem?: unknown }) => {
            const newItem = (value as { newItem?: unknown } | undefined)?.newItem as IReference | undefined
            closeItem()
            if (newItem) {
              setUpdatedAt()
              const items = replaceOrAddItem(newItem)
              setReferences(items)
              updateProgress()
            }
          }}
          header={header}
          noEscClose
        />
      )}
    </>
  )
}

export default withProfileCard(ReferencesCard as unknown as Parameters<typeof withProfileCard>[0])
